import React, { useCallback, useMemo } from 'react';
import { ExternalLink } from 'lucide-react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Breadcrumb } from '../components';
import { SignalInvestigationContent } from '../components/signals/SignalInvestigationContent';
import { useApp } from '../contexts/AppContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useTimeRange } from '../contexts/TimeRangeContext';
import { classifierLabel, getClassifierDefinition, useClassifierDefinitions } from '../services/classifiersApi';
import { components, palette } from '../utils/designTokens';
import { getSignalDescriptionFromKey } from '../utils/signalUtils';

export default function SignalInvestigationPage() {
  const { selectedAppId, classifierKey } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedEnvironment, environments } = useEnvironment();
  const { selectedRange, displayLabel } = useTimeRange();
  const { selectedAppName } = useApp();
  const { definitionsIndex } = useClassifierDefinitions(selectedAppId, {
    enabled: Boolean(selectedAppId),
  });

  const appName = useMemo(() => {
    if (selectedAppName) return selectedAppName;
    if (selectedAppId && environments.length) {
      for (const env of environments) {
        const app = env.apps?.find(a => a.id === selectedAppId);
        if (app) return app.name;
      }
    }
    return selectedAppId;
  }, [selectedAppId, selectedAppName, environments]);

  const signal = useMemo(() => {
    const definition = getClassifierDefinition(definitionsIndex, classifierKey);
    const label = searchParams.get('signal_label') || definition?.name || classifierLabel(classifierKey);
    const description = searchParams.get('signal_description') || definition?.description || getSignalDescriptionFromKey(classifierKey);
    return {
      key: classifierKey,
      classifierType: classifierKey,
      label,
      description,
      definition,
    };
  }, [classifierKey, definitionsIndex, searchParams]);

  const baseParams = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedEnvironment) params.set('env', selectedEnvironment);
    if (selectedRange) params.set('range', selectedRange);
    const startTime = searchParams.get('start_time');
    const endTime = searchParams.get('end_time');
    if (startTime) params.set('start_time', startTime);
    if (endTime) params.set('end_time', endTime);
    return params;
  }, [selectedEnvironment, selectedRange, searchParams]);

  const currentUrl = `${location.pathname}${location.search}`;
  const stateReferrerUrl = location.state?.referrerUrl || '';
  const stateReferrerLabel = location.state?.referrerLabel || '';
  const { breadcrumbItems, traceBreadcrumbItems } = useMemo(() => {
    const appParams = baseParams();
    const appQuery = appParams.toString() ? `?${appParams.toString()}` : '';
    const overviewParams = baseParams();
    overviewParams.set('tab', 'overview');
    const referrerUrl = stateReferrerUrl || `/app/${selectedAppId}?${overviewParams.toString()}`;
    const referrerLabel = stateReferrerLabel || 'Overview';

    return {
      breadcrumbItems: [
        { label: appName, to: `/app/${selectedAppId}${appQuery}` },
        { label: referrerLabel, to: referrerUrl },
        { label: signal.label },
      ],
      traceBreadcrumbItems: [
        { label: appName, to: `/app/${selectedAppId}${appQuery}` },
        { label: referrerLabel, to: referrerUrl },
        { label: signal.label, to: currentUrl },
      ],
    };
  }, [appName, baseParams, currentUrl, selectedAppId, signal.label, stateReferrerLabel, stateReferrerUrl]);

  const handleViewTraces = useCallback((group = null) => {
    const params = baseParams();
    params.set('signal', classifierKey);
    if (group?.primary_subtype) params.set('subtype', group.primary_subtype);
    if (group?.primary_event_key) params.set('event_key', group.primary_event_key);
    params.set('from', 'signal-investigation');
    navigate(`/app/${selectedAppId}/traces?${params.toString()}`, {
      state: {
        referrerUrl: currentUrl,
        referrerLabel: signal.label,
        referrerBreadcrumbItems: traceBreadcrumbItems,
      },
    });
  }, [baseParams, classifierKey, currentUrl, navigate, selectedAppId, signal.label, traceBreadcrumbItems]);

  const handleOpenTrace = useCallback((sample) => {
    const traceId = sample?.trace_id;
    if (!traceId) return;
    const params = baseParams();
    if (sample?.selected_span_id) params.set('span', sample.selected_span_id);
    navigate(`/app/${selectedAppId}/traces/${encodeURIComponent(traceId)}?${params.toString()}`, {
      state: {
        referrerUrl: currentUrl,
        referrerLabel: signal.label,
        referrerBreadcrumbItems: traceBreadcrumbItems,
      },
    });
  }, [baseParams, currentUrl, navigate, selectedAppId, signal.label, traceBreadcrumbItems]);

  return (
    <div className="flex h-full flex-1 flex-col gap-5">
      <Breadcrumb items={breadcrumbItems} />

      <div
        className="flex items-start justify-between gap-4 border-b pb-4"
        style={{ borderColor: palette.neutrals.borderSubtle }}
      >
        <div className="min-w-0">
          <div className="flex items-end gap-3">
            <h1 className="text-xl font-bold truncate" style={{ color: palette.text.heading }}>
              {signal.label}
            </h1>
            <span className="shrink-0 text-[11px]" style={{ color: palette.text.neutral }}>
              {selectedEnvironment} · {displayLabel}
            </span>
          </div>
          {signal.description && (
            <div className="mt-1 max-w-4xl text-sm" style={{ color: palette.text.muted }}>
              {signal.description}
            </div>
          )}
        </div>

        <button
          type="button"
          className="inline-flex shrink-0 items-center gap-1 rounded border px-2.5 py-1.5 text-xs font-medium"
          style={{
            borderColor: components.card.borderColor,
            color: palette.accents.primary.base,
            backgroundColor: palette.neutrals.panelSecondary,
          }}
          onClick={() => handleViewTraces(null)}
        >
          View all traces <ExternalLink size={13} />
        </button>
      </div>

      <SignalInvestigationContent
        selectedAppId={selectedAppId}
        signal={signal}
        onViewTraces={handleViewTraces}
        onOpenTrace={handleOpenTrace}
      />
    </div>
  );
}
